import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/PageHeader";
import HowItWorksButton from "../../components/howItWorks/HowItWorksButton";
import { useSettingsCounts } from "./hooks/useSettingsCounts";

import sitesIcon from "../../static/assets/icons/settings/Sites.svg";
import clientsIcon from "../../static/assets/icons/settings/Clients.svg";
import wasteIcon from "../../static/assets/icons/settings/Waste.svg";
import containersIcon from "../../static/assets/icons/settings/Containers.svg";
import camerasIcon from "../../static/assets/icons/settings/Cameras.svg";
import contactsIcon from "../../static/assets/icons/settings/Contacts.svg";
import constantsIcon from "../../static/assets/icons/settings/Constants.svg";

const SETTINGS_ICONS = {
  sites: sitesIcon,
  clients: clientsIcon,
  waste: wasteIcon,
  containers: containersIcon,
  cameras: camerasIcon,
  contacts: contactsIcon,
  constants: constantsIcon,
};

/** Display size for hub icons — Storybook: Foundations/Settings Hub Icons. */
const SETTINGS_HUB_ICON_SIZE = { width: 22, height: 18 };

const SettingsIcon = ({ type }) => {
  const src = SETTINGS_ICONS[type];
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      width={SETTINGS_HUB_ICON_SIZE.width}
      height={SETTINGS_HUB_ICON_SIZE.height}
      className="object-contain"
      aria-hidden
    />
  );
};

const SettingsHub = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { counts, isLoading } = useSettingsCounts();

  const getCountText = (id, count) => {
    if (isLoading || count === null) return "";
    const countValue = count || 0;
    const translationKeys = {
      sites: {
        one: "{{count}} active site",
        other: "{{count}} active sites",
      },
      clients: {
        one: "{{count}} active waste origin",
        other: "{{count}} active waste origins",
      },
      waste: {
        one: "{{count}} active flow",
        other: "{{count}} active flows",
      },
      containers: {
        one: "{{count}} active zone",
        other: "{{count}} active zones",
      },
      cameras: {
        one: "{{count}} active sensor",
        other: "{{count}} active sensors",
      },
      contacts: {
        one: "{{count}} active contact",
        other: "{{count}} active contacts",
      },
    };
    const keys = translationKeys[id];
    if (!keys) return "";
    const key = countValue === 0 || countValue === 1 ? keys.one : keys.other;
    return t(key, { count: countValue });
  };

  const settingsCards = [
    {
      id: "sites",
      icon: "sites",
      title: t("Sites"),
      description: t("settings_sites_description"),
      route: "/settings/sites",
      count: counts.sites,
    },
    {
      id: "clients",
      icon: "clients",
      title: t("Waste Origins"),
      description: t("settings_clients_description"),
      route: "/settings/clients",
      count: counts.clients,
    },
    {
      id: "waste",
      icon: "waste",
      title: t("Waste Flows"),
      description: t("settings_waste_description"),
      route: "/settings/waste",
      count: counts.waste,
    },
    {
      id: "containers",
      icon: "containers",
      title: t("Analysis Zones"),
      description: t("settings_containers_description"),
      route: "/settings/containers",
      count: counts.containers,
    },
    {
      id: "cameras",
      icon: "cameras",
      title: t("Sensors"),
      description: t("settings_cameras_description"),
      route: "/settings/cameras",
      count: counts.cameras,
    },
    {
      id: "contacts",
      icon: "contacts",
      title: t("Collection Contacts"),
      description: t("settings_contacts_description"),
      route: "/settings/contacts",
      count: counts.contacts,
    },
    {
      id: "constants",
      icon: "constants",
      title: t("My Constants"),
      description: t("settings_constants_description"),
      route: "/settings/constants",
    },
  ];

  const handleCardClick = (route) => {
    history.push(route);
  };

  return (
    <div className="settings-page-wrapper">
      <PageHeader
        title={t("My Settings")}
        helpButton={<HowItWorksButton topic="settingsHub" />}
      />
      <div className="settings-content-wrapper">
        <div className="flex flex-col items-center gap-6 w-full">
          {settingsCards.map((card) => (
            <div
              key={card.id}
              onClick={() => handleCardClick(card.route)}
              className="w-full max-w-[640px] h-[84px] bg-white border border-[#D3D3D3] rounded-lg flex items-center px-6 py-4 gap-4 cursor-pointer hover:border-[#00A7A6] hover:scale-[1.01] transition-all duration-200"
            >
              <div className="w-[52px] h-[52px] bg-[#E9FBFB] rounded-lg flex items-center justify-center flex-shrink-0">
                <SettingsIcon type={card.icon} />
              </div>
              <div className="flex flex-col justify-center flex-1 translate-y-1.5">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-[#222222] leading-6 mb-0.5">
                    {card.title}
                  </h3>
                  {card.count !== undefined && (
                    <span className="text-xs text-gray-500 leading-6 mb-0.5 italic ml-1">
                      {getCountText(card.id, card.count)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#222222] leading-[22px]">
                  {card.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SettingsHub;
